import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

import ROUTES from '../../routes';
import api from './../../config/axiosInstance';

import { showToast } from '../../utils/toast';
import { handleErrors } from '../../utils/common';

function AddBlog() {
    const formRef = useRef(null);
    const navigate = useNavigate();

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [img, setImg] = useState(null);
    const [isFeatured, setIsFeatured] = useState(false);

    const [imgPreview, setImgPreview] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const handleFormSubmit = async () => {
        const $ = window.jQuery;
        const form = $(formRef.current);

        if (form.valid()) {
            setTitle('');
            setDescription('');
            setImg(null);
            setImgPreview(null);

            $(form)
                .find('.is-valid, .is-invalid')
                .removeClass('is-valid is-invalid');

            $(form)
                .find('label.error, .invalid-feedback')
                .remove();

            const payload = { title, description, img, is_featured: isFeatured };

            try {
                setSubmitting(true);
                const res = await api.post(
                    '/blogs/create',
                    payload,
                    {
                        headers: {
                            "Content-Type": "multipart/form-data"
                        }
                    }
                );

                showToast('success', res.data.message, 'Success');
                navigate(ROUTES.BLOGS);
            } catch (err) {
                handleErrors(err);
            } finally {
                setSubmitting(false);
            }
        }
    };

    useEffect(() => {
        const $ = window.jQuery;

        $.validator.addMethod('extension', function (value, element, allowedExtensions) {
            if (!value) return true;
            var extension = value.split('.').pop().toLowerCase();
            return $.inArray(extension, allowedExtensions) !== -1;
        }, 'Invalid file type');

        $.validator.addMethod('ckeditorRequired', function (value) {
            const textOnly = value.replace(/<[^>]*>/g, '').trim();
            return textOnly.length > 0;
        }, 'This field is required');

        if (formRef.current && $.fn.validate) {
            $(formRef.current).validate({
                rules: {
                    title: {
                        required: true,
                        maxlength: 500
                    },
                    description: {
                        required: true,
                        ckeditorRequired: true
                    },
                    img: {
                        required: false,
                        extension: ['jpg', 'jpeg', 'png', 'svg', 'webp']
                    },
                    isFeatured: {
                        required: true
                    }
                },
                messages: {
                    title: {
                        required: "Please enter blog title",
                        maxlength: "Title cannot exceed 500 characters"
                    },
                    description: {
                        required: "Please enter blog description"
                    },
                    img: {
                        extension: "Only jpg, jpeg, png, svg, webp allowed"
                    },
                    isFeatured: {
                        required: "Please select is featured"
                    }
                },
                ignore: ".ck-editor *",
                errorElement: "label",
                validClass: "is-valid",
                errorClass: "is-invalid text-danger",
                highlight: function (element, errorClass, validClass) {
                    $(element).addClass(errorClass).removeClass(validClass);
                },
                unhighlight: function (element, errorClass, validClass) {
                    $(element).removeClass(errorClass).addClass(validClass);
                }
            });
        }
    }, []);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImg(file);
            setImgPreview(URL.createObjectURL(file));
        }
    };

    return (
        <>
            <div className="page-titles">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <Link to={ROUTES.BLOGS}>Blogs</Link>
                    </li>
                    <li className="breadcrumb-item active">
                        <Link to={ROUTES.ADD_BLOG}>Add Blog</Link>
                    </li>
                </ol>
            </div>

            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header">
                            <h4 className="card-title">Add Blog</h4>
                        </div>
                        <div className="card-body">
                            <form ref={formRef} noValidate>
                                <div className="row">
                                    <div className="mb-3 col-lg-6 col-md-6">
                                        <label className="form-label">Title<sup style={{ color: 'red' }}>*</sup></label>
                                        <input
                                            name="title"
                                            type="text"
                                            className="form-control"
                                            placeholder="Ex. How to maintain your car"
                                            value={title}
                                            onChange={(e) => setTitle(e.target.value)}
                                        />
                                    </div>
                                    <div className="mb-3 col-lg-6 col-md-6">
                                        <label htmlFor="img" className="form-label">Image</label>
                                        <div className="input-group custom_file_input">
                                            <div className="form-file">
                                                <input
                                                    name="img"
                                                    type="file"
                                                    className="form-file-input form-control"
                                                    onChange={handleImageChange}
                                                />
                                            </div>
                                            {/* <span className="input-group-text">Upload</span> */}
                                        </div>
                                        {imgPreview && (
                                            <div className="mt-2">
                                                <img
                                                    src={imgPreview}
                                                    alt="Preview"
                                                    style={{ width: 100, height: 100, objectFit: 'contain' }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div className="mb-3 col-lg-12">
                                        <label className="form-label">Description<sup style={{ color: 'red' }}>*</sup></label>
                                        <CKEditor
                                            editor={ClassicEditor}
                                            config={{
                                                toolbar: [
                                                    'heading', '|',
                                                    'bold', 'italic', 'link', 'code', 'codeBlock',
                                                    '|', 'bulletedList', 'numberedList'
                                                ]
                                            }}
                                            data={description}
                                            onChange={(event, editor) => {
                                                const data = editor.getData();
                                                setDescription(data);
                                                const hiddenInput = document.querySelector('input[name="description"]');
                                                if (hiddenInput) hiddenInput.value = data;
                                            }}
                                        />
                                        <input
                                            type="hidden"
                                            name="description"
                                            value={description}
                                        />
                                    </div>
                                    <div className="mb-3 col-lg-6 col-md-6">
                                        <label className="form-label">Is Featured</label>
                                        <select
                                            name="isFeatured"
                                            className="form-select"
                                            value={isFeatured}
                                            onChange={(e) => setIsFeatured(e.target.value)}
                                        >
                                            <option value="false">No</option>
                                            <option value="true">Yes</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="pt-3">
                                    <button
                                        type="button"
                                        onClick={handleFormSubmit}
                                        className="btn btn-sm btn-primary me-2"
                                        disabled={submitting}
                                    >
                                        {submitting ? (
                                            <>
                                                <i className="spinner-border spinner-border-sm me-1"></i>
                                                Submit
                                            </>
                                        ) : (
                                            <>
                                                <i className="fa fa-floppy-disk me-1"></i>
                                                Submit
                                            </>
                                        )}
                                    </button>
                                    <Link to={ROUTES.BLOGS} className="btn btn-sm btn-danger light">
                                        <i className="fas fa-times-circle"></i>&nbsp;
                                        Cancel
                                    </Link>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default AddBlog;
